import { FC } from 'react'
import Input from '@/components/Input'
import { apiClient } from 'src/apiMiddleware'
import { zodResolver } from '@hookform/resolvers/zod';
import schema from '@/validations/whitepaperInquirySchema'
import { useForm, useController, UseControllerProps } from 'react-hook-form';
import { classNames } from '@/utils'
import { PrivacyPolicy } from '@/route';

type Props = {
  title: string
  toThanks: (title: string) => void
}

type FormValues = {
  company: string
  email: string
  department: string
  firstName: string
  lastName: string
  phone: string
  title: string
  termCheck: boolean
}

type UseFormType = UseControllerProps<FormValues>

const Component: FC<Props> = ({ title, toThanks }) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: title },
    mode: "all",
  });

  const { field: { value, ...input }, fieldState: { invalid } } = useController({ name: 'termCheck', control })

  const onSubmit = async (data: FormValues) => {
    try {
      await apiClient.api.whitepaper_inquiry.$post({ body: data })
      reset()
      toThanks(title)
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col items-center gap-10" >
      <div className="font-semibold py-2 text-2xl self-start">資料請求</div>
      <div className="w-full flex flex-col gap-5">
        <div className="w-full flex gap-8 items-center py-2 border-b border-divided">
          <p className="text-xl font-semibold">会社情報</p>
          <p className="text-secondary-700">【必須】</p>
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="会社名" placeholder="DXHUB株式会社" name="company" control={control} />
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="部署名" placeholder="IoT事業部" name="department" control={control} />
        </div>
      </div>

      <div className="w-full flex flex-col gap-5"> <div className="w-full flex gap-8 items-center py-2 border-b border-divided"> <p className="text-xl font-semibold">担当者情報</p> <p className="text-secondary-700">【必須】</p>
      </div>

        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextSeparateForm label="氏名" placeholderFirst='姓' placeholderLast="名" lastName='lastName' firstName='firstName' control={control} name="firstName" />
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="メールアドレス" placeholder="お勤め先のメールアドレス" name="email" control={control} />
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="電話番号" placeholder="繋がりやすい電話番号" name="phone" control={control} />
        </div>
      </div>

      <div className="w-full flex flex-col gap-5">
        <div className="w-full flex flex-col items-start md:flex-row md:justify-between">
          <div />
          <div className="w-full md:w-[378px] items-center flex gap-2 md:gap-4">
            <input {...input}
              type="checkbox"
              className={classNames(
                "accent-primary-700",
                invalid
                  ? "ring-red-500 ring-4"
                  : ''
              )
              }
            />
            <div>
              <a className='text-blue-500' href={PrivacyPolicy} rel="noreferrer" target='_blank'>個人情報の取り扱いについて</a>
              <span>に同意する</span>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col items-center md:items-start md:flex-row md:justify-between">
          <div />
          <div className="w-full flex justify-center md:justify-start md:w-[378px]">
            <button disabled={!isValid || isSubmitting} type="submit" className="w-1/2 bg-accent-500 text-xl leading-none py-5 px-6 rounded-full shadow hover:shadow-md disabled:bg-gray-500/50">
              {isSubmitting
                ? <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24" >
                  <circle className="opacity-5" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                : <span>送信する</span>
              }
            </button>
          </div>
        </div>
      </div>
    </form >
  )
}

const TextForm: FC<{ label: string, placeholder: string } & UseFormType> = ({ label, placeholder, ...props }) => {
  const { field: input, fieldState: { invalid, error } } = useController(props)
  const message = error?.message === 'Required' ? '入力しましょう' : error?.message
  return (
    <>
      <label className="text-xl mb-2 md:mb-0">{label}</label>
      {/*@ts-ignore*/}
      <Input {...input} placeholder={placeholder} containerClass='bg-white w-full md:w-[378px] h-10' inputClass='bg-white px-4 text-lg' invalid={invalid} message={message} />
    </>
  )
}

const TextSeparateForm: FC<{ label: string, placeholderFirst: string, placeholderLast: string, lastName: 'lastName', firstName: 'firstName' } & UseFormType> = ({ label, placeholderFirst, placeholderLast, firstName, lastName, control }) => {
  const { field: firstField, fieldState: { invalid: ffInvalid } } = useController({ name: firstName, control })
  const { field: lastField, fieldState: { invalid: lfInvalid } } = useController({ name: lastName, control })
  return (
    <>
      <label className="text-xl">{label}</label>
      <div className="flex gap-4 w-full mt-2 md:mt-0 md:w-[378px]">
        <Input placeholder={placeholderFirst} containerClass='bg-white h-10' inputClass='bg-white px-4 text-lg' {...lastField} invalid={lfInvalid} />
        <Input placeholder={placeholderLast} containerClass='bg-white h-10' inputClass='bg-white px-4 text-lg' {...firstField} invalid={ffInvalid} />
      </div>
    </>
  )
}

export default Component
